<script setup lang="ts">
import { ref, computed, capitalize } from 'vue';
import type { Language } from '@ilteducation/content-ref-data-types/models/data-types';
import { useLocale } from '@/plugins/translation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DialogCloseButton } from '@ilteducation/components';

const props = withDefaults(
  defineProps<{
    color: string;
    languages: Language[];
  }>(),
  {
    color: 'primary',
  },
);
const emit = defineEmits<{
  (e: 'languageButtonClicked'): void;
  (e: 'languageSelected', language: Language): void;
}>();

const showLanguageDialog = ref(false);

const selectedLanguage = ref<Language>();
const handleLanguageSelection = (item: (typeof languageOptions.value)[0]): void => {
  const language = props.languages.find((l) => l.languageCode === item.value);
  if (!language) {
    return;
  }
  selectedLanguage.value = language;
  emit('languageSelected', language);
  showLanguageDialog.value = false;
};

const handleActivatorClick = (): void => {
  showLanguageDialog.value = true;
  emit('languageButtonClicked');
};

const languageName = (languageCode?: string): string => {
  if (!languageCode) {
    return '';
  }
  const key = `languages.${languageCode}`;
  const name = t(key);
  return name === key ? '' : name;
};

const languageOptions = computed(() =>
  props.languages.map((l) => ({
    title: capitalize(languageName(l.languageCode) || l.language),
    value: l.languageCode,
  })),
);

const { t, l } = useLocale();

const isActive = (item: { title: string; value: string }) => {
  return item.value === selectedLanguage?.value?.languageCode;
};
</script>

<template>
  <v-dialog
    role="dialog"
    aria-modal="true"
    v-model="showLanguageDialog"
    max-width="65vw"
    aria-labelledby="language_picker_dialog_title"
  >
    <template #activator="{ props }">
      <v-btn
        id="button_language_picker_dialog"
        class="d-flex justify-space-between"
        block
        v-bind="props"
        @click="handleActivatorClick"
      >
        <template #prepend>
          <FontAwesomeIcon icon="fa-regular fa-globe" />
          <p class="text-none ml-1">
            {{ languageName(selectedLanguage?.languageCode) || t(l.choose_language) }}
          </p>
        </template>
        <FontAwesomeIcon icon="fa-solid fa-caret-down"></FontAwesomeIcon>
      </v-btn>
    </template>
    <v-card class="px-16 py-8 align-center position-relative">
      <DialogCloseButton @click="showLanguageDialog = false" />
      <v-card-title id="language_picker_dialog_title">
        <FontAwesomeIcon icon="fa-regular fa-globe" class="mr-3" />
        {{ t(l.choose_language) }}
      </v-card-title>
      <v-card-subtitle style="opacity: 1">{{ t(l.area_guide.supporting_language_explainer) }}</v-card-subtitle>
      <v-list :aria-label="t(l.label_languages_list)" return-object width="90%" max-height="70vh" role="list">
        <li v-for="item in languageOptions" :key="item.title">
          <v-list-item
            :class="{ [`bg-${color}-lighten-3`]: isActive(item) }"
            @click.stop="handleLanguageSelection(item as (typeof languageOptions.value)[0])"
          >
            <template #prepend>
              <FontAwesomeIcon class="mr-2" v-if="isActive(item)" icon="fa-solid fa-check"></FontAwesomeIcon>
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </li>
      </v-list>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
